import { cn } from "@paribus/shared-ui";
import { Download, FileText, RefreshCw, Settings } from "lucide-react";

import type { MedChronExportMenuViewProps } from "./types";

export interface MedChronExportMenuInlineViewProps extends MedChronExportMenuViewProps {
  /** Matches the chronology header's icon-only vs icon+label breakpoint. */
  isCompact: boolean;
}

const formatButtonClassName =
  "flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-card text-neutral-700 shadow-sm ring-1 ring-neutral-200 transition-colors duration-200 hover:bg-surface-sunken hover:text-neutral-900 hover:ring-neutral-300 focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Chronology header presenter: Export PDF and Export DOCX stay directly clickable icon
 * buttons (no dropdown, per product decision — burying them would be a click regression).
 * "Export As" (the customization dialog) renders as a third direct button, only once
 * `onCustomize` is supplied. It is deliberately not labelled "Customize" — the header
 * already carries an unrelated "Customize reports" template button.
 */
export const MedChronExportMenuInlineView = ({
  isExportingPdf,
  isExportingDocx,
  disabled,
  onExportPdf,
  onExportDocx,
  onCustomize,
  isCompact,
}: MedChronExportMenuInlineViewProps) => {
  return (
    <>
      <button
        type="button"
        title="Export PDF"
        aria-label="Export PDF"
        className={cn(formatButtonClassName, !isCompact && "h-11 w-auto gap-2 px-4")}
        onClick={onExportPdf}
        disabled={disabled || isExportingPdf}
      >
        {isExportingPdf ? (
          <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        )}
        {!isCompact && <span className="text-sm font-bold">PDF</span>}
      </button>
      <button
        type="button"
        title="Export DOCX"
        aria-label="Export DOCX"
        className={cn(formatButtonClassName, !isCompact && "h-11 w-auto gap-2 px-4")}
        onClick={onExportDocx}
        disabled={disabled || isExportingDocx}
      >
        {isExportingDocx ? (
          <RefreshCw className="h-4 w-4 shrink-0 animate-spin text-chart-3" aria-hidden />
        ) : (
          <Download className="h-4 w-4 shrink-0 text-chart-3" aria-hidden />
        )}
        {!isCompact && <span className="text-sm font-bold">DOCX</span>}
      </button>
      {onCustomize && (
        <button
          type="button"
          title="Export As"
          aria-label="Export As"
          className={cn(formatButtonClassName, !isCompact && "h-11 w-auto gap-2 px-4")}
          onClick={onCustomize}
          disabled={disabled}
        >
          <Settings className="h-4 w-4 shrink-0" aria-hidden />
          {!isCompact && <span className="text-sm font-bold">Export As</span>}
        </button>
      )}
    </>
  );
};
